import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';

import { SETTINGS } from '@hotjar/webapp/constants/routes';
import { Button, Text, useNotify, tokens } from '@hotjar/hotsauce';
import { useIntegrations } from '../../hooks/use-integrations';
import { removeIntegration } from '../../domains/hubspot/api';
import type { OauthToken } from '../../domains/api';
import { IntegrationTokenName } from '../../domains/api';

interface HubspotActionsProps {
  siteId: string | null;
  actionLabel: Record<string, string>;
  isAdmin: boolean;
}

const UseOldAPIBox = styled.div`
  margin-top: ${tokens.spacer.x2};
`;

const OldAPICopy = styled(Text)`
  display: block;
  margin-bottom: ${tokens.spacer.x1};
`;

const getOAuthUrl = (siteId: string | null): string => {
  if (siteId !== null) {
    const redirectUri = encodeURIComponent(
      `${window.location.origin}${SETTINGS.INTEGRATIONS_HUBSPOT_OAUTH_REDIRECT.path}`,
    );

    return `https://app.hubspot.com/oauth/authorize?client_id=${window.settings.apis.hubspot.client_id}&redirect_uri=${redirectUri}&scope=timeline&state=${siteId}`;
  }

  return '#';
};

export const HubspotActions = ({
  siteId,
  actionLabel,
  isAdmin,
}: HubspotActionsProps): JSX.Element => {
  const { t } = useTranslation();
  const { showToast } = useNotify();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { integrations, refreshTokens } = useIntegrations(Number(siteId));

  const { settings = null } =
    integrations.find((integration) => integration.tokenName === IntegrationTokenName.Hubspot) ??
    {};

  const isProviderHubspot = (settings as OauthToken)?.provider === 'hubspot';
  const isProviderHubspotOauth2 = (settings as OauthToken)?.provider === 'hubspot-oauth2';
  const isEnabled = isProviderHubspot || isProviderHubspotOauth2;
  const useOldHubSpotAPI = isProviderHubspot && !isProviderHubspotOauth2;
  const oAuthSuccess = searchParams.get('oauth-success');

  useEffect(() => {
    if (!isEnabled && oAuthSuccess !== null) {
      if (oAuthSuccess === 'true') {
        refreshTokens();
        showToast({ text: t('integrations:hubspot.connected'), kind: 'success' });
      } else {
        showToast({ text: t('integrations:hubspot.connection-error'), kind: 'danger' });
      }
      setSearchParams({});
    }
  }, [isEnabled, oAuthSuccess, refreshTokens, setSearchParams, showToast, t]);

  const handleDisconnect = async (
    providerStr: string,
    optionalCallback?: (status: boolean) => void,
  ): Promise<void> => {
    try {
      const {
        data: { success },
      } = await removeIntegration(providerStr, String(siteId));
      if (optionalCallback) {
        optionalCallback(success);
      } else if (success) {
        refreshTokens();
        showToast({ text: t('integrations:hubspot.disconnected'), kind: 'success' });
      } else {
        showToast({ text: t('integrations:hubspot.disconnection-error'), kind: 'danger' });
      }
    } catch {
      showToast({ text: t('integrations:hubspot.disconnection-error'), kind: 'danger' });
    }
  };

  const hubspotMoveToOAuth2 = (): void => {
    const providerStr = 'hubspot';

    handleDisconnect(providerStr, (success: boolean) => {
      if (success) {
        refreshTokens();

        showToast({
          text: t('integrations:hubspot.old-integration-disconnected'),
          kind: 'success',
        });

        navigate(getOAuthUrl(siteId));
      } else {
        showToast({
          text: t('integrations:hubspot.old-integration-disconnection-error'),
          kind: 'danger',
        });
      }
    });
  };

  return (
    <>
      {isAdmin && !isEnabled ? (
        <Button
          aria-label={!isEnabled ? t(actionLabel.enable) : t(actionLabel.disable)}
          as="a"
          kind={!isEnabled ? 'primary' : 'danger_secondary'}
          href={getOAuthUrl(siteId)}
        >
          {!isEnabled ? t(actionLabel.enable) : t(actionLabel.disable)}
        </Button>
      ) : null}
      {isEnabled ? (
        <Button
          kind={!isEnabled ? 'primary' : 'danger_secondary'}
          isDisabled={!isAdmin}
          onClick={() => handleDisconnect('hubspot-oauth2')}
        >
          {!isEnabled ? t(actionLabel.enable) : t(actionLabel.disable)}
        </Button>
      ) : null}
      {useOldHubSpotAPI ? (
        <UseOldAPIBox>
          <OldAPICopy textType="subtitle2" textColor="text_danger">
            {t('integrations:hubspot.old-integration-connected')}
          </OldAPICopy>
          <Button isDisabled={!isAdmin} onClick={hubspotMoveToOAuth2}>
            {t('integrations:hubspot.reconnect-with-new-integration')}
          </Button>
        </UseOldAPIBox>
      ) : null}
    </>
  );
};
